import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  Loading,
  TextInput,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider, StoreTag } from '../dataProvider';

/**
 * Store-tag edit (Phase 8, D-multi-channel-editor). The label and description
 * are editable; the KEY is IMMUTABLE and shown read-only, because rate rules
 * reference the key (changing it would orphan a rule's scope). There is NO
 * delete. Saving calls `dataProvider.update('storeTags')`; the #25 wrapper
 * records the diff. Gated `stores:write`; a viewer is denied at the route.
 */

interface StoreTagEditValues {
  label: string;
  description: string;
}

const StoreTagEditForm = ({ record }: { record: StoreTag }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const defaultValues: StoreTagEditValues = {
    label: record.label,
    description: record.description,
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as StoreTagEditValues;
    const data = { label: values.label, description: values.description };
    const previousData = {
      label: record.label,
      description: record.description,
    };
    try {
      if (JSON.stringify(data) !== JSON.stringify(previousData)) {
        await dataProvider.update('storeTags', {
          id: record.id,
          data,
          previousData,
        });
      }
      notify('Tag updated', { type: 'success' });
      redirect('list', 'storeTags');
    } catch {
      notify('Could not save the tag', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.label}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            {/* The key is the stable rate-rule scope ref — read-only here. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Key (set at creation, immutable)
              </Typography>
              <Typography variant="body1" data-testid="store-tag-key">
                {record.key}
              </Typography>
            </Box>
            <TextInput
              source="label"
              label="Label"
              validate={required('Label is required')}
              fullWidth
            />
            <TextInput
              source="description"
              label="Description"
              validate={required('Description is required')}
              multiline
              fullWidth
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button type="button" onClick={() => redirect('list', 'storeTags')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const StoreTagEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<StoreTag>('storeTags', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <StoreTagEditForm record={record} />;
};

/** Gated entry point registered as the `storeTags` Resource's edit. */
export const StoreTagEdit = () => (
  <RequireAccess capability="stores:write">
    <StoreTagEditView />
  </RequireAccess>
);
